import { FHIRIdentifierCopier } from "@/components/DebugTools/FHIRIdentifierCopier";
import { Badge, BodyText, Collapsible } from "@/components/ui";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { LabTest } from "./LabTest";
import {
  type LabTestGroupItem,
  LabTestWorkflowStatus,
} from "./network/useLabTests";
interface LabTestProps {
  lab: LabTestGroupItem;
  patientId: string;
}

export const LabTestCollapsible = ({ lab, patientId }: LabTestProps) => {
  const { data: isLabResultSummaryUiEnabled } = useConfigCatFeatureFlag(
    "lab-result-summary-ui",
  );

  const statusBadge = useMemo(() => {
    if ([LabTestWorkflowStatus.COMPLETED].includes(lab.workflowStatus)) {
      return undefined;
    }

    let label = "";

    if (lab.workflowStatus === LabTestWorkflowStatus.NEEDS_DISCUSSION) {
      label = "Needs Discussion";
    } else if (lab.workflowStatus === LabTestWorkflowStatus.NEEDS_TRIAGE) {
      label = isLabResultSummaryUiEnabled ? "Needs Review" : "Needs Triage";
    } else if (
      lab.workflowStatus === LabTestWorkflowStatus.NEEDS_TRIAGE_PARTIAL
    ) {
      label = "Needs Triage Partial";
    } else if (lab.workflowStatus === LabTestWorkflowStatus.NEEDS_SUBMISSION) {
      label = "Needs Submission";
    } else if (
      lab.workflowStatus === LabTestWorkflowStatus.NEEDS_MANUAL_RESULTING
    ) {
      label = "Needs Manual Resulting";
    } else if (lab.workflowStatus === LabTestWorkflowStatus.FAILED) {
      label = "Failed to Order";
    }

    if (!label) {
      return undefined;
    }

    return (
      <Badge key="unconfirmed" variant="warning" className="whitespace-nowrap">
        <TriangleAlert className="w-3.5 stroke-warning" />
        <BodyText size="sm" textColor="warning">
          {label}
        </BodyText>
      </Badge>
    );
  }, [lab.workflowStatus, isLabResultSummaryUiEnabled]);

  const statusLabel = useMemo(() => {
    if (lab.workflowStatus === LabTestWorkflowStatus.COMPLETED) {
      return "Completed";
    }
    if (lab.workflowStatus === LabTestWorkflowStatus.ORDERED) {
      return "Ordered";
    }

    return undefined;
  }, [lab.workflowStatus]);

  return (
    <Collapsible
      title={lab.serviceRequest.testName as string}
      headerSize="sm"
      statusBadge={statusBadge}
      status={statusLabel}
      titleAction={
        <FHIRIdentifierCopier id={lab.serviceRequest.id as string} />
      }
    >
      <LabTest lab={lab} patientId={patientId} />
    </Collapsible>
  );
};
